// views/Geradoras/Analise/EsperadoRealizado.tsx
//
// "A usina está gerando o que o projeto prometeu?" — comparação mês a mês entre
// a geração medida e o orçamento energético do laudo PVsyst.
//
// Diferente do RankingUsinas, que compara as usinas ENTRE SI, aqui a régua é
// externa e fixa: o modelo assinado na engenharia. É a única visão da tela que
// responde à pergunta comercial (estamos entregando o contratado?) em vez da
// operacional (alguma usina está pior que as irmãs?). Por isso o atingimento
// vem como número grande com barra, e não como mais um KPI na fileira: é a
// conclusão do painel, não um dado dele.
//
// ⚠️ Três decisões que mudam a leitura dos números:
//
//   1. Barra = kWh, não kWh/kWp. O laudo dá energia; converter para kWh/kWp
//      exigiria escolher um denominador e o cadastro discorda do laudo (ver o
//      comentário longo em metaNoPeriodo). Energia contra energia dispensa
//      denominador.
//   2. O mês corrente aparece com a meta RATEADA pelos dias decorridos, senão
//      todo dia 1º o ano inteiro pareceria perdido.
//   3. Dia sem coleta NÃO tira o mês da conta. O contador do inversor é
//      acumulativo: quando o coletor volta, o delta do dia de retorno traz tudo
//      que foi gerado no intervalo mudo. Só saem da conta os meses sem baseline
//      (telemetria começou no meio) ou com contador resetado. Ver `comparavel`.
import React, { useState } from 'react';
import {
    ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, Cell,
} from 'recharts';
import { Target, TriangleAlert, FileText, Info, PencilLine } from 'lucide-react';
import {
    classificarAtingimento, PRESET_LABEL,
    type PresetPeriodo, type ResumoVsMeta, type StatusDesvio,
} from '../geradorasSeries';

/** Recortes que fazem sentido para uma meta mensal e sazonal. Nada abaixo de um mês. */
const PRESETS_META: PresetPeriodo[] = ['ano_atual', '12m', 'mes_atual', 'mes_anterior'];

const COR_REAL = '#F6A937';          // secondary da marca: é o que a usina entregou
const COR_REAL_PARCIAL = '#fcd9a0';  // mesmo tom, lavado: mês ainda aberto
const COR_META = '#94a3b8';          // cinza: o modelo é referência, não protagonista
const COR_P90 = '#5F6C37';           // primary: o piso contratual

const MES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** mes_ref vem como 'YYYY-MM-DD'. Fatiar a string evita o -1 dia do fuso. */
const rotuloMes = (mesRef: string): string => {
    const [ano, mes] = mesRef.split('-');
    return `${MES_CURTO[Number(mes) - 1]}/${ano.slice(2)}`;
};

const fmtKwh = (v: number | null): string => {
    if (v == null) return '—';
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} GWh`;
    if (Math.abs(v) >= 1000) return `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MWh`;
    return `${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh`;
};

const ESTILO: Record<StatusDesvio, { texto: string; chip: string; barra: string; label: string }> = {
    acima: {
        texto: 'text-emerald-600 dark:text-emerald-400',
        chip: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
        barra: 'bg-emerald-500',
        label: 'Acima do esperado',
    },
    esperado: {
        texto: 'text-primary',
        chip: 'bg-primary/10 text-primary border-primary/20',
        barra: 'bg-primary',
        label: 'No esperado',
    },
    abaixo: {
        texto: 'text-amber-600 dark:text-amber-400',
        chip: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
        barra: 'bg-amber-500',
        label: 'Abaixo do esperado',
    },
    critico: {
        texto: 'text-rose-600 dark:text-rose-400',
        chip: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800',
        barra: 'bg-rose-500',
        label: 'Muito abaixo',
    },
    sem_referencia: {
        texto: 'text-gray-400',
        chip: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600',
        barra: 'bg-gray-300 dark:bg-gray-600',
        label: 'Sem laudo',
    },
};

const TooltipCustom: React.FC<any> = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const est = ESTILO[classificarAtingimento(d.pct)];
    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl px-3 py-2 text-xs max-w-[15rem]">
            <div className="font-bold text-gray-900 dark:text-white mb-1.5">{label}</div>
            <div className="space-y-0.5 tabular-nums">
                {[
                    { rot: 'Gerado', v: d.real, forte: true },
                    { rot: 'Esperado', v: d.meta, forte: false },
                    { rot: 'Mínimo provável', v: d.p90, forte: false },
                ].map(l => (
                    <div key={l.rot} className="flex justify-between gap-4">
                        <span className="text-gray-500 dark:text-gray-400">{l.rot}</span>
                        <span className={l.forte
                            ? 'font-bold text-gray-900 dark:text-white'
                            : 'font-semibold text-gray-600 dark:text-gray-300'}>
                            {fmtKwh(l.v)}
                        </span>
                    </div>
                ))}
            </div>
            <div className={`mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-700 font-bold ${est.texto}`}>
                {d.pct == null ? '—' : `${d.pct.toFixed(1)}% do esperado`} · {est.label}
            </div>
            {d.parcial && (
                <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    Mês em andamento: a meta foi rateada pelos dias já decorridos.
                </div>
            )}
            {d.diasSemDado > 0 && (
                <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    {d.diasSemDado} dia(s) sem coleta. Não falta energia na conta: o contador do
                    inversor é acumulativo e o delta do retorno traz o intervalo inteiro.
                </div>
            )}
        </div>
    );
};

interface Props {
    resumo: ResumoVsMeta;
    /** Usinas na seleção atual — para dizer quantas o laudo cobre. */
    totalUsinasSelecionadas: number;
    /** Proveniência (campo `fonte` da meta): que laudo, que versão, que data. */
    fonte?: string | null;
    periodoLabel: string;
    preset: PresetPeriodo;
    onTrocarPreset: (p: PresetPeriodo) => void;
    /** site_id_api -> nome legível do bloco. Sem isto a tabela por bloco mostraria hashes. */
    nomePorSite?: Record<string, string>;
}

const EsperadoRealizado: React.FC<Props> = ({
    resumo, totalUsinasSelecionadas, fonte, periodoLabel, preset, onTrocarPreset, nomePorSite,
}) => {
    const cobertas = resumo.sitesComMeta.length;
    const [visao, setVisao] = useState<'mes' | 'usina'>('mes');

    // Segmentado próprio, e não os chips do topo da tela: o período da meta é
    // outro do período da curva, e um controle compartilhado obrigaria a
    // escolher entre "hoje hora a hora" e "o ano contra o laudo".
    const seletor = (
        <div className="flex items-center gap-0.5 p-0.5 rounded-xl bg-gray-100 dark:bg-gray-700/60 shrink-0">
            {PRESETS_META.map(p => (
                <button
                    key={p}
                    onClick={() => onTrocarPreset(p)}
                    aria-pressed={preset === p}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all whitespace-nowrap ${preset === p
                        ? 'bg-white dark:bg-gray-800 text-primary shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
                >
                    {PRESET_LABEL[p]}
                </button>
            ))}
        </div>
    );

    if (cobertas === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <h3 className="font-poppins font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Target size={16} className="text-gray-300 dark:text-gray-600" />
                        Esperado x realizado
                    </h3>
                    {seletor}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Nenhuma usina desta seleção tem laudo PVsyst cadastrado no período. Hoje só os cinco
                    blocos de Morrinhos têm — as demais aparecem aqui quando o relatório delas for carregado.
                </p>
            </div>
        );
    }

    const dados = resumo.meses.map(m => ({
        rotulo: rotuloMes(m.mesRef),
        real: m.realKwh,
        meta: m.metaKwh,
        p90: m.metaP90Kwh,
        pct: m.atingimentoPct,
        parcial: m.parcial,
        diasSemDado: m.diasSemDado,
    }));

    const pct = resumo.atingimentoPct;
    const est = ESTILO[classificarAtingimento(pct)];
    const deficit = resumo.totalMetaKwh - resumo.totalRealKwh;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="font-poppins font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Target size={16} className="text-primary" />
                        Esperado x realizado
                    </h3>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        Geração medida contra o orçamento do laudo PVsyst · {periodoLabel}
                        {cobertas < totalUsinasSelecionadas && (
                            <> · <span className="font-semibold">{cobertas} de {totalUsinasSelecionadas}</span> usinas com laudo</>
                        )}
                    </p>
                </div>
                {seletor}
            </div>

            {/* Veredito do período: UM percentual, e só dos meses comparáveis.
                Os absolutos ao lado são de todos os meses — energia que entrou é
                energia que entrou, mesmo em mês com coletor caído. */}
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-0.5">
                            Do esperado, no período
                        </div>
                        <div className="flex items-baseline gap-2.5 flex-wrap">
                            <span className={`text-4xl font-poppins font-bold tabular-nums leading-none ${est.texto}`}>
                                {pct == null ? '—' : `${pct.toFixed(1)}%`}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${est.chip}`}>
                                {est.label}
                            </span>
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            {resumo.mesesComparaveis} {resumo.mesesComparaveis === 1 ? 'mês medido' : 'meses medidos'}
                            {resumo.mesesForaDaConta > 0 && (
                                <> · <span className="text-amber-600 dark:text-amber-400 font-semibold">
                                    {resumo.mesesForaDaConta} fora da conta
                                </span> por mês incompleto</>
                            )}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">
                            Gerado no período
                        </div>
                        <div className="text-2xl font-poppins font-bold tabular-nums text-gray-900 dark:text-white leading-tight">
                            {fmtKwh(resumo.totalRealKwh)}
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                            de {fmtKwh(resumo.totalMetaKwh)} esperados
                            <span className={deficit >= 0 ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-emerald-600 dark:text-emerald-400 font-semibold'}>
                                {' '}({deficit >= 0 ? '−' : '+'}{fmtKwh(Math.abs(deficit))})
                            </span>
                        </div>
                    </div>
                </div>

                {/* Barra até o esperado, com o traço do mínimo provável. */}
                <div className="mt-3 relative h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-700 ${est.barra}`}
                        style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
                    />
                    {resumo.totalMetaKwh > 0 && (
                        <div
                            title="Mínimo provável (P90) do laudo"
                            className="absolute top-0 bottom-0 w-px bg-gray-500/70 dark:bg-gray-300/60"
                            style={{ left: `${(resumo.totalMetaP90Kwh / resumo.totalMetaKwh) * 100}%` }}
                        />
                    )}
                </div>
            </div>

            <div className="p-5">
                <div style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={dados} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#37415122" vertical={false} />
                            <XAxis dataKey="rotulo" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={8} />
                            <YAxis
                                tick={{ fontSize: 11 }}
                                // 72, nao 58: com Morrinhos inteira o topo do eixo passa de
                                // 1.000 MWh e "1200 MWh" era cortado no primeiro caractere,
                                // aparecendo como "200 MWh" acima de "900 MWh".
                                width={72}
                                tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} MWh` : String(v)}
                            />
                            <Tooltip content={<TooltipCustom />} cursor={{ fill: '#94a3b81a' }} />
                            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} iconType="circle" iconSize={8} />

                            <Bar dataKey="meta" name="Esperado" fill={COR_META} fillOpacity={0.35} radius={[4, 4, 0, 0]} maxBarSize={44} />
                            <Bar dataKey="real" name="Gerado" fill={COR_REAL} radius={[4, 4, 0, 0]} maxBarSize={44}>
                                {/* Mês em andamento sai lavado: o número é real, mas ainda não fechou. */}
                                {dados.map((d, i) => (
                                    <Cell key={i} fill={d.parcial ? COR_REAL_PARCIAL : COR_REAL} />
                                ))}
                            </Bar>
                            <Line
                                type="monotone" dataKey="p90" name="Mínimo provável (P90)"
                                stroke={COR_P90} strokeWidth={2} strokeDasharray="5 4"
                                dot={false} activeDot={{ r: 4 }}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>

                {/* O gráfico mostra a forma; a tabela dá o número que vai para a
                    reunião. Duas leituras da mesma verdade: "que mês foi ruim" e
                    "que bloco puxou para baixo" — a segunda é a que manda alguém
                    a campo. */}
                <div className="mt-5 flex items-center gap-0.5 p-0.5 rounded-xl bg-gray-100 dark:bg-gray-700/60 w-fit">
                    {([['mes', 'Por mês'], ['usina', 'Por bloco']] as const).map(([v, rot]) => (
                        <button
                            key={v}
                            onClick={() => setVisao(v)}
                            aria-pressed={visao === v}
                            className={`px-3 py-1 rounded-lg text-[11px] font-bold transition-all ${visao === v
                                ? 'bg-white dark:bg-gray-800 text-primary shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
                        >
                            {rot}
                        </button>
                    ))}
                </div>

                <div className="mt-3 -mx-1 overflow-x-auto">
                    <table className="w-full text-xs min-w-[32rem]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700">
                                <th className="py-2 px-1 font-bold">{visao === 'mes' ? 'Mês' : 'Bloco'}</th>
                                <th className="py-2 px-1 font-bold text-right">Gerado</th>
                                <th className="py-2 px-1 font-bold text-right">Esperado</th>
                                <th className="py-2 px-1 font-bold text-right">Atingido</th>
                                <th className="py-2 px-1 font-bold">Situação</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-gray-700/40">
                            {visao === 'mes' ? resumo.meses.map(m => {
                                const e = ESTILO[classificarAtingimento(m.atingimentoPct)];
                                return (
                                    <tr key={m.mesRef} className="hover:bg-gray-50/70 dark:hover:bg-gray-900/25 transition-colors">
                                        <td className="py-2 px-1 font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">
                                            {rotuloMes(m.mesRef)}
                                            {m.parcial && <span className="ml-1.5 text-[9px] text-gray-400 font-normal">em andamento</span>}
                                            {m.ajustadoManual && (
                                                <span
                                                    title="Valor corrigido à mão em Config > Geração e Metas"
                                                    className="ml-1.5 text-[9px] text-amber-600 dark:text-amber-400 font-bold"
                                                >
                                                    ajustado
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 px-1 text-right tabular-nums font-semibold text-gray-900 dark:text-white">{fmtKwh(m.realKwh)}</td>
                                        <td className="py-2 px-1 text-right tabular-nums text-gray-500 dark:text-gray-400">{fmtKwh(m.metaKwh)}</td>
                                        <td className={`py-2 px-1 text-right tabular-nums font-bold ${m.comparavel ? e.texto : 'text-gray-400 dark:text-gray-500'}`}>
                                            {m.atingimentoPct == null ? '—' : `${m.atingimentoPct.toFixed(0)}%`}
                                        </td>
                                        <td className="py-2 px-1">
                                            {m.comparavel ? (
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold border whitespace-nowrap ${e.chip}`}>
                                                        {e.label}
                                                    </span>
                                                    {m.diasSemDado > 0 && (
                                                        <span
                                                            title={`${m.diasSemDado} dia(s) sem coleta neste mês. Não afeta o total: o contador do inversor é acumulativo.`}
                                                            className="text-[9px] font-semibold text-gray-400 dark:text-gray-500 whitespace-nowrap"
                                                        >
                                                            {m.diasSemDado}d sem coleta
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span
                                                    title="Sem leitura anterior a este mês (a telemetria começou no meio dele) ou contador do inversor resetado. Nos dois casos o total do mês está incompleto e ele fica fora do percentual do período."
                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border whitespace-nowrap bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800"
                                                >
                                                    <TriangleAlert size={9} className="stroke-[3]" />
                                                    Mês incompleto
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            }) : resumo.porUsina.map(u => {
                                const e = ESTILO[classificarAtingimento(u.atingimentoPct)];
                                return (
                                    <tr key={u.site_id_api} className="hover:bg-gray-50/70 dark:hover:bg-gray-900/25 transition-colors">
                                        <td className="py-2 px-1 font-semibold text-gray-800 dark:text-gray-200">
                                            {nomePorSite?.[u.site_id_api] ?? u.site_id_api}
                                        </td>
                                        <td className="py-2 px-1 text-right tabular-nums font-semibold text-gray-900 dark:text-white">{fmtKwh(u.realKwh)}</td>
                                        <td className="py-2 px-1 text-right tabular-nums text-gray-500 dark:text-gray-400">{fmtKwh(u.metaKwh)}</td>
                                        <td className={`py-2 px-1 text-right tabular-nums font-bold ${e.texto}`}>
                                            {u.atingimentoPct == null ? '—' : `${u.atingimentoPct.toFixed(0)}%`}
                                        </td>
                                        <td className="py-2 px-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold border whitespace-nowrap ${e.chip}`}>
                                                    {e.label}
                                                </span>
                                                {u.diasSemDado > 0 && (
                                                    <span
                                                        title={`${u.diasSemDado} dia(s) sem leitura no período inteiro`}
                                                        className="inline-flex items-center gap-1 text-[9px] font-bold text-amber-600 dark:text-amber-400 whitespace-nowrap"
                                                    >
                                                        <TriangleAlert size={9} className="stroke-[3]" />
                                                        -{u.diasSemDado}d
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {visao === 'usina' && (
                    <p className="mt-2 text-[10px] text-gray-400 dark:text-gray-500">
                        Gerado e esperado somam o período inteiro; o percentual usa só os meses
                        comparáveis de cada bloco.
                    </p>
                )}

                {resumo.mesesForaDaConta > 0 && (
                    <div className="mt-4 rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/15 px-4 py-3 flex items-start gap-2">
                        <TriangleAlert size={14} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-gray-700 dark:text-gray-300">
                            <b>{resumo.mesesForaDaConta} mês(es) fora do percentual.</b> Neles a telemetria
                            começou no meio do mês ou o contador do inversor resetou, e o total ficou
                            incompleto de verdade. Continuam no gráfico e nos totais em kWh — só não
                            entram na média, que ficaria pessimista sem motivo.
                        </p>
                    </div>
                )}

                {resumo.temLacuna && resumo.mesesForaDaConta === 0 && (
                    <div className="mt-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/30 px-4 py-3 flex items-start gap-2">
                        <Info size={14} className="text-gray-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                            <b>Há dias sem coleta no período</b>, mas nenhuma energia falta na conta: o
                            contador do inversor é acumulativo e o delta do dia em que o coletor volta traz
                            o intervalo inteiro. O indicador fica aqui como saúde da integração.
                        </p>
                    </div>
                )}

                {resumo.temAjusteManual && (
                    <p className="mt-3 flex items-start gap-1.5 text-[10px] text-amber-600 dark:text-amber-400">
                        <PencilLine size={11} className="mt-px shrink-0" />
                        <span>
                            Há mês com geração corrigida à mão (Config &gt; Geração e Metas). O motivo de
                            cada correção fica registrado lá, junto com quem fez.
                        </span>
                    </p>
                )}

                {fonte && (
                    <p className="mt-3 flex items-start gap-1.5 text-[10px] text-gray-400 dark:text-gray-500">
                        <FileText size={11} className="mt-px shrink-0" />
                        <span>
                            Régua: {fonte}. Ano meteorológico típico, não previsão do ano corrente — o laudo
                            declara 3,1% de variação de um ano para o outro.
                        </span>
                    </p>
                )}
            </div>
        </div>
    );
};

export default EsperadoRealizado;
